import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { IoIosRepeat } from 'react-icons/io';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import Pagination from '../../../components/pagination/Pagination';
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent';
import SearchSelect from '../../../components/textFields/SearchSelect';
import TextField from '../../../components/textFields/TextField';
import DateRangeComp from '../../../components/dateComponent/DateRangeComp';
import DateComp from '../../../components/dateComponent/DateComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu';
import { useGetCreditCardRequestedUsersQuery } from '../../../services/authApi';
import { constraintConfig } from '../../../constraintConfig';
import labelKey from "../../localization/label.json";
import UpdateCreditCardStatusModal from './UpdateCreditCardStatusModal';
import CreditCardRequestUserDetailModal from './CreditCardRequestUserDetailModal';
import UserDetailModal from '../users/UserDetailModal';
import { KTSVG } from '../../../_metronic/helpers';
import useColumnVisibility from '../../customHooks/useColumnVisibility';
import CreditCardRequestStatusComp from '../../../components/StatusComp/CreditCardRequestStatusComp';

const ACK_STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: String(constraintConfig.ccCardRequestStatus.RequestApproved), label: 'RequestApproved' },
    { value: String(constraintConfig.ccCardRequestStatus.RequestRejected), label: 'RequestRejected' },
    { value: String(constraintConfig.ccCardRequestStatus.CreditCardOrdered), label: 'CreditCardOrdered' },
    { value: String(constraintConfig.ccCardRequestStatus.Cancel), label: 'Cancel' },
];

const COLUMN_IDS = [
    'checkbox', 'firstName', 'department', 'designation', 'cellPhone', 'userType', 'expenseType',
    'location', 'shipToAddress', 'spendingLimit', 'division', 'ccRequestedDate', 'ccAssignedApprover',
    'ccApprovedDate', 'ccApprovedBy', 'ccRejectedDate', 'ccRejectedBy', 'ccOrderedDate', 'modifiedOn',
    'modifiedBy', 'status', 'actions',
] as const;

const CreditCardRequestsUsersApprovalQueue = () => {
    const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'acknowledgeCreditCardRequests' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchStr, setSearchStr] = useState('');
    const [sortBy, setSortBy] = useState('ccRequestedDate');
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortedColumn, setSortedColumn] = useState('ccRequestedDate');
    const [filterByStatus, setFilterByStatus] = useState('');
    const [ccApprovedFrom, setCcApprovedFrom] = useState('');
    const [ccApprovedTo, setCcApprovedTo] = useState('');
    const [ccRejectedFrom, setCcRejectedFrom] = useState('');
    const [ccRejectedTo, setCcRejectedTo] = useState('');
    const [ccOrderedFrom, setCcOrderedFrom] = useState('');
    const [ccOrderedTo, setCcOrderedTo] = useState('');
    const [filterByApprovedBy, setFilterByApprovedBy] = useState('');
    const [filterByRejectedBy, setFilterByRejectedBy] = useState('');

    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
    const [selectedUserData, setSelectedUserData] = useState<any>(null);
    const [updateStatusUserIds, setUpdateStatusUserIds] = useState<number[]>([]);
    const [updateStatusRows, setUpdateStatusRows] = useState<any[]>([]);

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailRequestId, setDetailRequestId] = useState<number | null>(null);
    const [showUserDetailModal, setShowUserDetailModal] = useState(false);
    const [userDetailData, setUserDetailData] = useState<any>(null);

    const { data, isFetching: isLoading, isSuccess, refetch } = useGetCreditCardRequestedUsersQuery({
        pageNumber: page,
        pageSize: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        searchStr: searchStr,
        filterByStatus: filterByStatus,
        filterByDepartment: '',
        filterByDesignation: '',
        ccApprovedFrom: ccApprovedFrom || undefined,
        ccApprovedTo: ccApprovedTo || undefined,
        ccRejectedFrom: ccRejectedFrom || undefined,
        ccRejectedTo: ccRejectedTo || undefined,
        ccOrderedFrom: ccOrderedFrom || undefined,
        ccOrderedTo: ccOrderedTo || undefined,
        filterByApprovedBy: filterByApprovedBy || undefined,
        filterByRejectedBy: filterByRejectedBy || undefined,
    });

    const openDetail = (orgUserCreditCardRequestId?: number) => {
        if (!orgUserCreditCardRequestId) {
            toast.warning('Request detail is not available for this row');
            return;
        }
        setDetailRequestId(orgUserCreditCardRequestId);
        setShowDetailModal(true);
    };

    const openUserDetail = (userAccountId?: number) => {
        if (!userAccountId) {
            toast.warning('User detail is not available for this row');
            return;
        }
        setUserDetailData(userAccountId);
        setShowUserDetailModal(true);
    };

    const handlePageSizeChange = (e: any) => {
        setPageSize(parseInt(e.target.value, 10));
        setPage(1);
    };

    const handlePageClick = (e: any) => {
        setPage(e.selected + 1);
    };

    const handleSearchChange = (e: any) => {
        setSearchStr(e.target.value);
        setPage(1);
    };

    const handleSort = (property: string) => {
        setSortBy(property);
        if (sortedColumn === property) {
            setSortOrder(prevSortOrder => (prevSortOrder === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortedColumn(property);
            setSortOrder('asc');
        }
    };

    const formatRangeValue = (from: string, to: string) =>
        from && to
            ? `${moment(from).format('MM/DD/YYYY')} - ${moment(to).format('MM/DD/YYYY')}`
            : 'MM/DD/YYYY';

    const handleApprovedRangeChange = (_event: any, picker: any) => {
        setCcApprovedFrom(moment(picker.startDate).format('YYYY-MM-DD'));
        setCcApprovedTo(moment(picker.endDate).format('YYYY-MM-DD'));
        setPage(1);
    };
    const handleApprovedRangeCancel = () => {
        setCcApprovedFrom('');
        setCcApprovedTo('');
        setPage(1);
    };

    const handleRejectedRangeChange = (_event: any, picker: any) => {
        setCcRejectedFrom(moment(picker.startDate).format('YYYY-MM-DD'));
        setCcRejectedTo(moment(picker.endDate).format('YYYY-MM-DD'));
        setPage(1);
    };
    const handleRejectedRangeCancel = () => {
        setCcRejectedFrom('');
        setCcRejectedTo('');
        setPage(1);
    };

    const handleOrderedRangeChange = (_event: any, picker: any) => {
        setCcOrderedFrom(moment(picker.startDate).format('YYYY-MM-DD'));
        setCcOrderedTo(moment(picker.endDate).format('YYYY-MM-DD'));
        setPage(1);
    };
    const handleOrderedRangeCancel = () => {
        setCcOrderedFrom('');
        setCcOrderedTo('');
        setPage(1);
    };

    const handleItemSelect = (userId: number) => {
        setSelectedItems(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            }
            return [...prev, userId];
        });
        setSelectAll(false);
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([]);
        } else {
            const allIds = data?.result?.data?.map((item: any) => item.userAccountID) || [];
            setSelectedItems(allIds);
        }
        setSelectAll(!selectAll);
    };

    const handleUpdateStatusClick = () => {
        if (selectedItems.length === 0) {
            toast.warning('Please select at least one user to update status');
            return;
        }

        const rows = (data?.result?.data || []).filter((item: any) =>
            selectedItems.includes(item.userAccountID)
        );
        const selectedUser = rows[0];

        if (selectedUser) {
            setSelectedUserData(selectedUser);
            setUpdateStatusUserIds(selectedItems);
            setUpdateStatusRows(rows);
            setShowUpdateStatusModal(true);
        }
    };

    const handleRowUpdateStatus = (row: any) => {
        if (!row?.userAccountID) {
            toast.warning('Unable to update status for this row');
            return;
        }
        setSelectedUserData(row);
        setUpdateStatusUserIds([row.userAccountID]);
        setUpdateStatusRows([row]);
        setShowUpdateStatusModal(true);
    };

    useEffect(() => {
        setSelectedItems([]);
        setSelectAll(false);
    }, [data?.result?.data]);

    const [prevShowUpdateStatusModal, setPrevShowUpdateStatusModal] = useState(false);

    useEffect(() => {
        setPrevShowUpdateStatusModal(showUpdateStatusModal);
    }, [showUpdateStatusModal]);

    useEffect(() => {
        if (prevShowUpdateStatusModal && !showUpdateStatusModal) {
            refetch();
            setSelectedItems([]);
            setSelectAll(false);
        }
    }, [showUpdateStatusModal, prevShowUpdateStatusModal, refetch]);

    useEffect(() => {
        setPage(1);
    }, [searchStr, filterByStatus, ccApprovedFrom, ccApprovedTo, ccRejectedFrom, ccRejectedTo, ccOrderedFrom, ccOrderedTo, filterByApprovedBy, filterByRejectedBy]);

    const handleRefresh = () => {
        refetch()
            .then(() => {
                if (isSuccess) {
                    toast.success('Data Refresh successfully!');
                }
            })
            .finally(() => {
                setSelectedItems([]);
                setSelectAll(false);
            });
    };

    const clearFilters = () => {
        setFilterByStatus('');
        setCcApprovedFrom('');
        setCcApprovedTo('');
        setCcRejectedFrom('');
        setCcRejectedTo('');
        setCcOrderedFrom('');
        setCcOrderedTo('');
        setFilterByApprovedBy('');
        setFilterByRejectedBy('');
        setSearchStr('');
        setPage(1);
    };

    const isVisible = (columnId: string) => hiddenColumns.includes(columnId);

    const visibleColCount = useMemo(() => {
        const dataCols = COLUMN_IDS.filter((id) => id !== 'actions' && hiddenColumns.includes(id)).length;
        return dataCols + 1;
    }, [hiddenColumns]);

    const renderSortHeader = (property: string, label: string, className = 'min-w-120px') => (
        <th className={className} onClick={() => handleSort(property)} style={{ cursor: 'pointer' }}>
            <div className='table-arrow'>
                {label}
                {sortedColumn === property ? (sortOrder === 'desc' ? <FaArrowDown /> : <FaArrowUp />) : ''}
            </div>
        </th>
    );

    const columnToggles: { id: string; label: string }[] = [
        { id: 'checkbox', label: 'Select' },
        { id: 'firstName', label: labelKey.name },
        { id: 'department', label: labelKey.department },
        { id: 'designation', label: labelKey.designation },
        { id: 'cellPhone', label: labelKey.cellPhone },
        { id: 'userType', label: 'User Type' },
        { id: 'expenseType', label: 'Allowed Expense Type' },
        { id: 'location', label: 'Office Location' },
        { id: 'shipToAddress', label: 'Shipping Address' },
        { id: 'spendingLimit', label: 'Corp CC Limit' },
        { id: 'division', label: 'Division' },
        { id: 'ccRequestedDate', label: 'CC Requested Date' },
        { id: 'ccAssignedApprover', label: 'Corp CC Approver' },
        { id: 'ccApprovedDate', label: 'Corporate Credit Card Approved' },
        { id: 'ccApprovedBy', label: 'Corp Credit Card Approved By' },
        { id: 'ccRejectedDate', label: 'Corporate Credit Card Rejected' },
        { id: 'ccRejectedBy', label: 'Corp Credit Card Rejected By' },
        { id: 'ccOrderedDate', label: 'Corporate Credit Card Ordered' },
        { id: 'modifiedOn', label: 'Modified On' },
        { id: 'modifiedBy', label: 'Modified By' },
        { id: 'status', label: 'Status' },
        { id: 'actions', label: labelKey.action },
    ];

    return (
        <>
            <UpdateCreditCardStatusModal
                show={showUpdateStatusModal}
                handleClose={() => {
                    setShowUpdateStatusModal(false);
                    setUpdateStatusUserIds([]);
                    setUpdateStatusRows([]);
                }}
                data={selectedUserData}
                selectedUserIds={updateStatusUserIds}
                selectedRows={updateStatusRows}
            />

            <CreditCardRequestUserDetailModal
                show={showDetailModal}
                handleClose={() => {
                    setShowDetailModal(false);
                    setDetailRequestId(null);
                }}
                orgUserCreditCardRequestId={detailRequestId}
            />
            <UserDetailModal
                show={showUserDetailModal}
                handleClose={() => {
                    setShowUserDetailModal(false);
                    setUserDetailData(null);
                }}
                data={userDetailData}
            />

            <Tooltip id="refreshData" place="top" />
            <Tooltip id="updateStatus" place="top" />
            <Tooltip id="viewDetail" place="top" />
            <Tooltip id="userDetail" place="top" />

            <div className={`card`}>
                <div className='card-header border-0 flex-column align-items-stretch pt-4'>
                    <Alert variant="primary" className="mb-3">
                        <p className='fs-6 mb-0'>
                            Shows approved, rejected, ordered, and cancelled corporate credit card requests for acknowledgment. Select users to update status (for example, mark a card as ordered), and use date filters to review requests by approved, rejected, or ordered date.
                        </p>
                    </Alert>
                    <div className='d-flex flex-wrap gap-3 align-items-end mb-3'>
                        <div style={{ minWidth: 220 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Credit Card Request Status:</label>
                            <SearchSelect
                                options={ACK_STATUS_OPTIONS}
                                value={ACK_STATUS_OPTIONS.find(o => o.value === filterByStatus) || ACK_STATUS_OPTIONS[0]}
                                onChange={(e: any) => setFilterByStatus(e?.value != null ? String(e.value) : '')}
                                placeholder='All Statuses'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 260 }}>
                            <DateRangeComp
                                label='CC Approved Date Range'
                                startDate={ccApprovedFrom ? moment(ccApprovedFrom) : moment().subtract(6, 'days')}
                                endDate={ccApprovedTo ? moment(ccApprovedTo) : moment()}
                                onApply={handleApprovedRangeChange}
                                onCancel={handleApprovedRangeCancel}
                                value={formatRangeValue(ccApprovedFrom, ccApprovedTo)}
                            />
                        </div>
                        <div style={{ minWidth: 260 }}>
                            <DateRangeComp
                                label='Rejected Date Range'
                                startDate={ccRejectedFrom ? moment(ccRejectedFrom) : moment().subtract(6, 'days')}
                                endDate={ccRejectedTo ? moment(ccRejectedTo) : moment()}
                                onApply={handleRejectedRangeChange}
                                onCancel={handleRejectedRangeCancel}
                                value={formatRangeValue(ccRejectedFrom, ccRejectedTo)}
                            />
                        </div>
                        <div style={{ minWidth: 260 }}>
                            <DateRangeComp
                                label='Ordered Date Range'
                                startDate={ccOrderedFrom ? moment(ccOrderedFrom) : moment().subtract(6, 'days')}
                                endDate={ccOrderedTo ? moment(ccOrderedTo) : moment()}
                                onApply={handleOrderedRangeChange}
                                onCancel={handleOrderedRangeCancel}
                                value={formatRangeValue(ccOrderedFrom, ccOrderedTo)}
                            />
                        </div>
                        <div style={{ minWidth: 220 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Corp Credit Card Approved By:</label>
                            <input
                                type='text'
                                className='form-control form-control-solid'
                                placeholder='Approved By...'
                                value={filterByApprovedBy}
                                onChange={(e) => setFilterByApprovedBy(e.target.value)}
                            />
                        </div>
                        <div style={{ minWidth: 220 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Corp Credit Card Rejected By:</label>
                            <input
                                type='text'
                                className='form-control form-control-solid'
                                placeholder='Rejected By...'
                                value={filterByRejectedBy}
                                onChange={(e) => setFilterByRejectedBy(e.target.value)}
                            />
                        </div>
                        <button type='button' className='btn btn-sm btn-light' onClick={clearFilters}>Clear</button>
                    </div>

                    <div className='d-flex justify-content-between align-items-center'>
                        <div className='d-flex gap-1 align-items-center'>
                            <SearchBarComponent
                                className='me-4'
                                placeholder='User'
                                value={searchStr}
                                onChange={handleSearchChange}
                            />
                            <button
                                data-tooltip-id="refreshData"
                                data-tooltip-content='Refresh Data'
                                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                onClick={handleRefresh}
                            >
                                <IoIosRepeat className='h-20px w-20px' />
                            </button>
                        </div>
                        <div className='card-title d-flex align-items-center gap-3 mb-0'>
                            {selectedItems.length > 0 && (
                                <span className="badge badge-success me-2">
                                    {selectedItems.length} selected
                                </span>
                            )}
                            <button
                                data-tooltip-id="updateStatus"
                                data-tooltip-content='Update status for selected users'
                                className='btn btn-primary btn-sm'
                                onClick={handleUpdateStatusClick}
                                disabled={selectedItems.length === 0}
                            >
                                Update Status
                            </button>
                        </div>
                    </div>
                </div>
                <div className='card-body py-3'>
                    <div className='table-responsive table-height'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    {isVisible('checkbox') && (
                                        <th className='min-w-40px rounded-start text-center'>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                                disabled={!data?.result?.data?.length || isLoading}
                                            />
                                        </th>
                                    )}
                                    {isVisible('firstName') && renderSortHeader('firstName', labelKey.name)}
                                    {isVisible('department') && renderSortHeader('department', labelKey.department)}
                                    {isVisible('designation') && renderSortHeader('designation', labelKey.designation)}
                                    {isVisible('cellPhone') && renderSortHeader('cellPhone', labelKey.cellPhone, 'min-w-140px')}
                                    {isVisible('userType') && renderSortHeader('userType', 'User Type')}
                                    {isVisible('expenseType') && renderSortHeader('expenseType', 'Allowed Expense Type', 'min-w-140px')}
                                    {isVisible('location') && renderSortHeader('location', 'Office Location')}
                                    {isVisible('shipToAddress') && renderSortHeader('shipToAddress', 'Shipping Address', 'min-w-160px')}
                                    {isVisible('spendingLimit') && renderSortHeader('spendingLimit', 'Corp CC Limit', 'min-w-100px text-end')}
                                    {isVisible('division') && renderSortHeader('division', 'Division', 'min-w-100px')}
                                    {isVisible('ccRequestedDate') && renderSortHeader('ccRequestedDate', 'CC Requested Date', 'min-w-120px text-center')}
                                    {isVisible('ccAssignedApprover') && renderSortHeader('ccAssignedApprover', 'Corp CC Approver', 'min-w-160px')}
                                    {isVisible('ccApprovedDate') && renderSortHeader('ccApprovedDate', 'Corporate Credit Card Approved', 'min-w-120px text-center')}
                                    {isVisible('ccApprovedBy') && renderSortHeader('ccApprovedBy', 'Corp Credit Card Approved By', 'min-w-140px')}
                                    {isVisible('ccRejectedDate') && renderSortHeader('ccRejectedDate', 'Corporate Credit Card Rejected', 'min-w-120px text-center')}
                                    {isVisible('ccRejectedBy') && renderSortHeader('ccRejectedBy', 'Corp Credit Card Rejected By', 'min-w-140px')}
                                    {isVisible('ccOrderedDate') && renderSortHeader('ccOrderedDate', 'Corporate Credit Card Ordered', 'min-w-120px text-center')}
                                    {isVisible('modifiedOn') && renderSortHeader('modifiedOn', 'Modified On', 'min-w-140px text-center')}
                                    {isVisible('modifiedBy') && renderSortHeader('modifiedBy', 'Modified By', 'min-w-120px')}
                                    {isVisible('status') && renderSortHeader('status', 'Status', 'min-w-100px')}
                                    <th className='min-w-80px rounded-end pe-4 text-end border-0'>
                                        {isVisible('actions') && 'Action'}
                                        <TableSettingMenu>
                                            {columnToggles.map((col) => (
                                                <TextField
                                                    key={col.id}
                                                    rightLabel={col.label}
                                                    type="checkbox"
                                                    onChange={() => handleToggleColumn(col.id)}
                                                    checked={isVisible(col.id)}
                                                    fieldClass='mb-4'
                                                />
                                            ))}
                                        </TableSettingMenu>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {!isLoading && (
                                    <>
                                        {data?.result?.data?.length > 0 ? (
                                            data.result.data.map((row: any, index: any) => (
                                                <tr key={row.orgUserCreditCardRequestId || row.userAccountID || index}>
                                                    {isVisible('checkbox') && (
                                                        <td className='text-center'>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedItems.includes(row.userAccountID)}
                                                                onChange={() => handleItemSelect(row.userAccountID)}
                                                            />
                                                        </td>
                                                    )}
                                                    {isVisible('firstName') && (
                                                        <td>
                                                            <button
                                                                type='button'
                                                                className='btn btn-link p-0 text-start'
                                                                onClick={() => openDetail(row?.orgUserCreditCardRequestId)}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                                                        <div className="symbol-label fs-3 bg-light-info text-info text-capitalize">{row?.firstName?.charAt(0)}</div>
                                                                    </div>
                                                                    <div className="d-flex flex-column">
                                                                        <span className="text-gray-800 fw-semibold mb-1 text-capitalize">{row?.firstName} {row?.middleName} {row?.lastName}</span>
                                                                        <span className='text-lowercase'>{row?.email}</span>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        </td>
                                                    )}
                                                    {isVisible('department') && <td>{row?.department?.title || 'N/A'}</td>}
                                                    {isVisible('designation') && <td>{row?.designation?.title || 'N/A'}</td>}
                                                    {isVisible('cellPhone') && <td>{row?.cellPhone || 'N/A'}</td>}
                                                    {isVisible('userType') && <td>{row?.userType?.value || 'N/A'}</td>}
                                                    {isVisible('expenseType') && <td>{row?.expenseType?.value || 'N/A'}</td>}
                                                    {isVisible('location') && <td>{row?.location?.value || 'N/A'}</td>}
                                                    {isVisible('shipToAddress') && <td>{row?.shipToAddress || 'N/A'}</td>}
                                                    {isVisible('spendingLimit') && (
                                                        <td className="text-end">
                                                            {row?.spendingLimit != null && Number(row.spendingLimit) > 0
                                                                ? <CurrencyComp amount={row.spendingLimit} />
                                                                : 'N/A'}
                                                        </td>
                                                    )}
                                                    {isVisible('division') && <td>{row?.division?.title || 'N/A'}</td>}
                                                    {isVisible('ccRequestedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccRequestedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('ccAssignedApprover') && (
                                                        <td>
                                                            <div className='text-capitalize'>{row?.ccAssignedApproverName || 'N/A'}</div>
                                                            {row?.ccAssignedApproverEmail && (
                                                                <div className='text-muted text-lowercase fs-7'>{row.ccAssignedApproverEmail}</div>
                                                            )}
                                                        </td>
                                                    )}
                                                    {isVisible('ccApprovedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccApprovedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('ccApprovedBy') && (
                                                        <td>
                                                            <div className='text-capitalize'>{row?.ccApprovedByUserName || 'N/A'}</div>
                                                            {row?.ccApprovedByUserEmail && (
                                                                <div className='text-muted text-lowercase fs-7'>{row.ccApprovedByUserEmail}</div>
                                                            )}
                                                        </td>
                                                    )}
                                                    {isVisible('ccRejectedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccRejectedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('ccRejectedBy') && (
                                                        <td>
                                                            <div className='text-capitalize'>{row?.ccRejectedByUserName || 'N/A'}</div>
                                                            {row?.ccRejectedByUserEmail && (
                                                                <div className='text-muted text-lowercase fs-7'>{row.ccRejectedByUserEmail}</div>
                                                            )}
                                                        </td>
                                                    )}
                                                    {isVisible('ccOrderedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccOrderedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('modifiedOn') && (
                                                        <td className='text-center'>
                                                            <DateTimeComp formattedDate={row?.ccRequestModifiedOn} />
                                                        </td>
                                                    )}
                                                    {isVisible('modifiedBy') && <td className='text-capitalize'>{row?.ccRequestModifiedBy || 'N/A'}</td>}
                                                    {isVisible('status') && (
                                                        <td>
                                                            <CreditCardRequestStatusComp
                                                                statusId={row?.creditCardCardRequest?.id}
                                                                statusTitle={row?.creditCardCardRequest?.title}
                                                            />
                                                        </td>
                                                    )}
                                                    <td className='text-end'>
                                                        {isVisible('actions') && (
                                                            <div className='d-flex align-items-center justify-content-end gap-2'>
                                                                <button
                                                                    type='button'
                                                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                                                    data-tooltip-id='viewDetail'
                                                                    data-tooltip-content='View Corporate credit card request detail'
                                                                    onClick={() => openDetail(row?.orgUserCreditCardRequestId)}
                                                                >
                                                                    <i className='fas fa-eye'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                                                    data-tooltip-id='updateStatus'
                                                                    data-tooltip-content='Update Status'
                                                                    onClick={() => handleRowUpdateStatus(row)}
                                                                >
                                                                    <i className='fas fa-edit'></i>
                                                                </button>
                                                                <div
                                                                    data-tooltip-id='userDetail'
                                                                    data-tooltip-content='User Details'
                                                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                                                    onClick={() => openUserDetail(row?.userAccountID || row?.orgUserId)}
                                                                >
                                                                    <KTSVG
                                                                        path='/media/icons/duotune/arrows/arr064.svg'
                                                                        className='svg-icon-3'
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={visibleColCount}><NoRecordFound /></td>
                                            </tr>
                                        )}
                                    </>
                                )}
                                {isLoading && (
                                    <tr>
                                        <td colSpan={visibleColCount} className="text-center"><LoadingComponent /></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
                        toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
                        ofResult={data?.result?.totalRecords}
                        onChange={handlePageSizeChange}
                        pageSize={pageSize}
                        onPageChange={handlePageClick}
                        pageCount={data?.result?.totalPages || 0}
                    />
                </div>
            </div>
        </>
    )
}

export default CreditCardRequestsUsersApprovalQueue;